// hooks/useJobEconomics.js
// Single source of truth for Hauler-facing payout math.
// Wraps calcSplit() (market-aware cost inputs) with a duration estimate
// so JobCard / JobDetailScreen / CompletionModal / EarningsScreen all
// read from one shape.
//
// Safety invariant 1 (CLAUDE.md): one earnings number per context, sourced
// from useJobEconomics() only. Components must not duplicate calcSplit().
//
// Bucket A+B scope: no surge, no viability floor enforcement, no snapshot
// lock. Snapshot persistence happens at completion time elsewhere.

import { useMemo } from "react";
import { calcSplit, getTierPrice } from "../utils/marketConfig";
import { getCustomerTierLabel, DEFAULT_MARKET_ID } from "../utils/common";

// Tier → estimated minutes on-site + drive. Heuristic; refined by
// observed completion durations in Phase 4.3 (Pending).
const DURATION_BY_TIER = {
  Small:  20,
  Half:   30,
  ThreeQ: 45,
  Full:   90,
};

/**
 * @param {object} job - request row from supabase, or a synthetic preview.
 *   Expected fields: size (tier key), market_id, zone, payout_snapshot (optional).
 * @returns single shape consumed by all Hauler-facing surfaces.
 */
export function useJobEconomics(job) {
  return useMemo(() => deriveJobEconomics(job), [
    job?.id,
    job?.size,
    job?.market_id,
    job?.zone,
    job?.quick,
    job?.mattress_count,
    job?.payout_snapshot,
  ]);
}

export function deriveJobEconomics(job) {
  if (!job || !job.size) {
    return null;
  }

  const tierKey = job.size;
  const tierLabel = getCustomerTierLabel(tierKey) || "—";
  const marketId = job.market_id || DEFAULT_MARKET_ID;
  const zoneKey = job.zone || null;

  // Prefer a persisted payout_snapshot (post-completion) over a live calc.
  // The snapshot is the historical record. We do NOT show "locked at
  // snapshot" framing in Bucket A+B; the value is just used as-is.
  if (job.payout_snapshot && typeof job.payout_snapshot === "object") {
    const snap = job.payout_snapshot;
    const driverPay = Number(snap.driverPay ?? snap.driver_pay ?? 0);
    return {
      tierKey,
      tierLabel,
      gross: Number(snap.gross ?? 0),
      stripeFee: Number(snap.stripeFee ?? snap.stripe_fee ?? 0),
      dumpFee: Number(snap.dumpFee ?? snap.dump_fee ?? 0),
      fuelEst: Number(snap.fuelEst ?? snap.fuel_est ?? 0),
      companyMargin: Number(snap.companyMargin ?? snap.company_margin ?? 0),
      driverPay,
      durationMin: DURATION_BY_TIER[tierKey] || 30,
      hourly: hourlyEstimate(driverPay, DURATION_BY_TIER[tierKey] || 30),
      viable: driverPay > 0,
      fromSnapshot: true,
    };
  }

  // Live calculation. Resolve gross from market table; tolerate missing
  // market/zone with a safe fallback so a misconfigured row never blows
  // up the whole jobs list.
  let split;
  let gross;
  try {
    gross = getTierPrice(marketId, tierKey) ?? 0;
    split = calcSplit(gross, marketId, zoneKey, { quick: job.quick === true, mattressPieces: job.mattress_count });
  } catch {
    return {
      tierKey,
      tierLabel,
      gross: 0,
      stripeFee: 0,
      dumpFee: 0,
      fuelEst: 0,
      companyMargin: 0,
      driverPay: 0,
      durationMin: DURATION_BY_TIER[tierKey] || 30,
      hourly: 0,
      viable: false,
      fromSnapshot: false,
    };
  }

  const durationMin = DURATION_BY_TIER[tierKey] || 30;

  return {
    tierKey,
    tierLabel,
    gross: split.gross,
    stripeFee: split.stripeFee,
    dumpFee: split.dumpFee,
    fuelEst: split.fuelEst,
    companyMargin: split.companyMargin,
    driverPay: split.driverPay,
    durationMin,
    hourly: hourlyEstimate(split.driverPay, durationMin),
    viable: split.driverPay > 0,
    fromSnapshot: false,
  };
}

function hourlyEstimate(pay, minutes) {
  if (!pay || !minutes) return 0;
  return Math.round((pay / (minutes / 60)) * 100) / 100;
}

// Back-compat export, now derived from the canonical label helper so it can
// never drift from getCustomerTierLabel().
export const HAULER_TIER_LABEL = ["Small", "Half", "ThreeQ", "Full"].reduce(
  (acc, key) => ({ ...acc, [key]: getCustomerTierLabel(key) }),
  {}
);
